import React from 'react';
import PropTypes from 'prop-types';

import { Redirect } from 'react-router';

import { makeStyles } from '@material-ui/core/styles';

import {
  Link,
  Tooltip,
  Typography,
} from '@material-ui/core';

import {
  FileCopyOutlined as CopyIcon,
} from '@material-ui/icons';

import {
  UserContext,
} from "../../user";

import {
  fmtClient 
} from "../../client";

import { 
  ResourcesContext,
} from "..";

import { 
  ResourceChip,
} from ".";

import {
  CopyToClipboard,
} from 'react-copy-to-clipboard';

const useStyles = makeStyles((theme) => ({
  displayValue: {
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'row',
  },
  placeholder: {
    minWidth: '20px',
  },
}));

export function fmtClientResourceRef(resourceIdOrArrayOfIds) {
  return(<ResourceRef value={resourceIdOrArrayOfIds} type="Client"/>);
}

export function fmtDatasetResourceRef(resourceIdOrArrayOfIds) {
  return(<ResourceRef value={resourceIdOrArrayOfIds} type="Dataset"/>);
}

export function fmtDatastoreResourceRef(resourceIdOrArrayOfIds) {
  return(<ResourceRef value={resourceIdOrArrayOfIds} type="Datastore"/>);
}

export function fmtLandingZoneResourceRef(resourceIdOrArrayOfIds) {
  return(<ResourceRef value={resourceIdOrArrayOfIds} type="LandingZone"/>);
}

//
// value:   either a string representing a resourceId or a props object
//          that carries the required resource id, as well as optional qualifiers 
//          like name, desription and extLink.
//
//          Example:
//          { 
//            id: "123", 
//            name: "My Resource",
//            description: "This is a very nice resource, you should take a look.",
//            extLink: {
//              href: "https://...",
//              icon: <React.Component>
//           }
//
// type:    one of the supported resoure types
// variant: passed down to Typography, defaults to 'inherit'
// linked:  if true, the ref name will be rendered as a hyperlink that will
//          redirect to the resource card in console
// sorted:  if true, ids will be ordered by (inferred) 'name', otherwise they are 
//          in order found in 'value'
// format:  if 'csv', names will be rendered as a single comma delimited list of 
//          strings, if 'multiline' names will be rendered one per line
//          if 'qualified' each ref will be rendered in a separate component 
//          including optional 'description' and 'extLink' if present.  External
//          link will be rendered as an icon button on right end of ref and will 
//          popup href in a separate window. optional 'icon' may be used.
// adorned: if true and format is 'qualified' the rendered ref will include a 
//          resource type icon on left end if component
//

export default function ResourceRef(props) {
  const { value, type, variant, linked, copyable, sorted, show, format, adorned } = props;

  const [ navToResource, setNavToResource ] = React.useState(null);
  const [ copied, setCopied ] = React.useState(false);
  const [ showCopy, setShowCopy ] = React.useState(false);

  const classes = useStyles();
  
  const resourcesContext=React.useContext(ResourcesContext);
  const userContext=React.useContext(UserContext);
  const refValues=value ? (value.constructor === Array?value:[value]) : [];
  let refs=refValues.map((rv) => {
    let ref=(typeof rv === 'object')?rv:{ id: rv };
    if(ref.ResourceId && !ref.id) {
      ref.id=ref.ResourceId;
    }
    if(ref.Description && !ref.name) {
      ref.name=ref.Description;
    }
    if(type==='Client' && !ref.name && show==='default') {
      ref.name=fmtClient(ref.id,userContext);
    }
    if(!ref.name) {
      ref.name=ref.id;
      if(show==='default') {
        const resource=resourcesContext.resources.find((resource) => resource.ResourceId===ref.id);
        if(resource) {
          ref.name=resource.Description;
        }
      }
    }
    return ref;
  });
  if(navToResource) {
    return <Redirect push to={`/resources/${type}s/${navToResource}`.toLowerCase()} />;  
  }
  if(sorted) {
    refs=refs.sort((a,b)=>(a.name>b.name)?1:-1);
  }

  if(format==='qualified') {
    return(
      <React.Fragment>
        {
          refs.map((ref, index) => {
            return(
              <ResourceChip key={index} type={type} linked={linked} adorned={adorned} {...ref}/>
            );
          })
        }
      </React.Fragment>
    );
  } else {
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', flexDirection: format==='csv'?'row':'column'}}>
        {
          refs.map((ref, index) => {
            return(
              <React.Fragment key={index}>
                {(index>0 && format==='csv')?<Typography variant={variant}>,&nbsp;</Typography>:null}
                <Tooltip title={ref.id !== ref.name ?
                      <Typography variant={variant}>{type+" ID: "+ref.id}</Typography>
                    :""} enterDelay={1000} leaveDelay={200} arrow>
                    <div className={classes.displayValue} 
                        onMouseEnter={() => setShowCopy(copyable)} 
                        onMouseLeave={() => {setShowCopy(false); setCopied(false);}}>
                      {linked?
                        <Link onClick={() => setNavToResource(ref.id)}>
                          <Typography variant={variant}>{ref.name}</Typography>
                        </Link>
                        :
                        <Typography variant={variant}>{ref.name}</Typography>
                      }
                      {copyable?showCopy?
                        <Tooltip title={copied?"Copied":"Copy to clipboard"} placement="top-start" arrow>
                          <CopyToClipboard text={value} onCopy={() => setCopied(true)}>
                            <CopyIcon fontSize='small' onMouseLeave={() => setCopied(false)}/>
                          </CopyToClipboard>
                        </Tooltip>:
                        <div className={classes.placeholder}/>:null  
                      }
                    </div>
                </Tooltip>
              </React.Fragment>
            );
          })
        }
      </div>
    );
  }
}

ResourceRef.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string,PropTypes.array,PropTypes.object]).isRequired,
  type: PropTypes.oneOf(['Client','Credential','Dataset','Datastore','LandingZone','Stream','Subscription','Workload']).isRequired,
  variant: PropTypes.string.isRequired,
  linked: PropTypes.bool.isRequired,
  copyable: PropTypes.bool.isRequired,
  sorted: PropTypes.bool.isRequired,
  show: PropTypes.oneOf(['default', 'id_only']).isRequired,
  format: PropTypes.oneOf(['csv','multiline','qualified']).isRequired,
  adorned: PropTypes.bool.isRequired,
  extLink: PropTypes.string,
};

ResourceRef.defaultProps = {
  variant: 'inherit',
  linked: true,
  copyable: false,
  sorted: false,
  show: 'default',
  adorned: false,
  format: 'csv'
};
